// Libs
import classNames from 'classnames/bind';
import {
  BaseButton,
  Button,
  Dialog,
  IconArrowOpenDownSolid,
  IconArrowOpenEndSolid,
  IconToggleStartLine,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
// Others
import { IBaseSelectOption } from '~/utils/interface/common';
import { adminRoute } from '~/utils/routes/admin';
import { useAppSelector } from '~/utils/redux/hooks';
import { studentRoute } from '~/utils/routes/student';
import { EMPTY_STRING } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './MobileMenuDialog.module.scss';
import { icons } from '~/assets';

type Props = {
  isMenuOpen: boolean;
  isMenuClose: boolean;
  onClose: () => void;
  onHelpClick: () => void;
  onChangeLanguage: (option: IBaseSelectOption) => void;
  onChangeCourse: (id: string) => void;
  languageOptions: IBaseSelectOption[];
  languageSelected: string;
  courseSelected: string;
  onLogout: () => void;
  showLogout?: boolean;
  showAdminMenu?: boolean;
};

const cx = classNames.bind(styles);

const MobileMenuDialog = (props: Props) => {
  //#region Destructuring Props
  const {
    isMenuOpen,
    isMenuClose,
    onClose,
    onHelpClick,
    onChangeLanguage,
    onChangeCourse,
    languageOptions,
    languageSelected,
    courseSelected,
    onLogout,
    showLogout = true,
    showAdminMenu = false,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/navbar', 'common/base']);
  const navigate = useNavigate();
  const location = useLocation();
  //#endregion Declare Hook

  //#region Selector
  const { mainCourses } = useAppSelector((state) => state.courseState);
  //#endregion Selector

  //#region Declare State
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isCourseOpen, setIsCourseOpen] = useState(false);
  const isStudentPage = location.pathname.startsWith(studentRoute.base);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const toggleLanguage = () => {
    setIsLanguageOpen(!isLanguageOpen);
  };

  const toggleCourse = () => {
    setIsCourseOpen(!isCourseOpen);
  };

  const handleNavigateTuitionArea = () => {
    navigate(`${adminRoute.base}${adminRoute.tuitionArea}`);
    onClose();
  };

  const handleNavigateAdministrativeArea = () => {
    navigate(`${adminRoute.base}${adminRoute.administrativeArea}`);
    onClose();
  };
  //#endregion Handle Function

  return (
    <Dialog
      open={isMenuOpen}
      onDismiss={onClose}
      shouldCloseOnDocumentClick
      className={cx('menuDialog', { open: isMenuOpen, close: isMenuClose })}
    >
      <div className={cx('menuModal')}>
        <div className={cx('closeIcon')} onClick={onClose}>
          <img
            src={icons.commonIconClose}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className={cx('closeIcon')}
          />
        </div>

        <div className={cx('menuContent')}>
          {showAdminMenu && (
            <>
              <BaseButton
                onClick={handleNavigateTuitionArea}
                renderIcon={
                  <img
                    src={icons.commonIconTuitionArea}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('menuIcon', 'tuitionIcon')}
                  />
                }
                textAlign='start'
                color='primary-inverse'
                withBorder={false}
              >
                <span className={cx('menuItemTitle')}>{t('tuition_area')}</span>
              </BaseButton>

              <BaseButton
                onClick={handleNavigateAdministrativeArea}
                renderIcon={
                  <img
                    src={icons.commonIconExamArea}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('menuIcon', 'administrativeIcon')}
                  />
                }
                textAlign='start'
                color='primary-inverse'
                withBorder={false}
              >
                <span className={cx('menuItemTitle')}>{t('administrative_area')}</span>
              </BaseButton>
            </>
          )}

          {currentRole === RoleEnum.TEACHER && isStudentPage && (
            <>
              <BaseButton
                onClick={toggleCourse}
                renderIcon={
                  <>
                    <img
                      src={icons.commonIconCourse}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      className={cx('menuIcon', 'changeCourseIcon')}
                    />

                    <div className={cx('menuItemIcon')}>
                      {isCourseOpen ? (
                        <IconArrowOpenDownSolid size='x-small' color='brand' />
                      ) : (
                        <IconArrowOpenEndSolid size='x-small' color='brand' />
                      )}
                    </div>
                  </>
                }
                color='primary-inverse'
                allowTransparency={true}
                aria-expanded={isCourseOpen}
                textAlign='start'
                withBorder={false}
              >
                <span className={cx('menuItemTitle')}>{t('select_course_label')}</span>
              </BaseButton>

              {isCourseOpen && (
                <View as='div' className={cx('subMenu')}>
                  {mainCourses.map((course) => (
                    <BaseButton
                      as='div'
                      key={course.id}
                      onClick={() => onChangeCourse(course.id)}
                      display='block'
                      textAlign='start'
                      withBorder={false}
                      withBackground={false}
                    >
                      <span className={cx('subMenuItem', { active: +courseSelected === +course.id })}>
                        {course.name}
                      </span>
                    </BaseButton>
                  ))}
                </View>
              )}
            </>
          )}

          <BaseButton
            onClick={onHelpClick}
            renderIcon={
              <img
                src={icons.commonIconHelp}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('menuIcon', 'helpIcon')}
              />
            }
            textAlign='start'
            color='primary-inverse'
            withBorder={false}
          >
            <span className={cx('menuItemTitle')}>{t('tooltip_action_help')}</span>
          </BaseButton>

          <BaseButton
            onClick={toggleLanguage}
            renderIcon={
              <>
                <img
                  src={icons.commonIconChangeLanguage}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('menuIcon', 'changeLanguageIcon')}
                />

                <div className={cx('menuItemIcon')}>
                  {isLanguageOpen ? (
                    <IconArrowOpenDownSolid size='x-small' color='brand' />
                  ) : (
                    <IconArrowOpenEndSolid size='x-small' color='brand' />
                  )}
                </div>
              </>
            }
            color='primary-inverse'
            allowTransparency={true}
            aria-expanded={isLanguageOpen}
            textAlign='start'
            withBorder={false}
          >
            <span className={cx('menuItemTitle')}>{t('tooltip_action_language')}</span>
          </BaseButton>

          {isLanguageOpen && (
            <View as='div' className={cx('subMenu')}>
              {languageOptions.map((option) => (
                <BaseButton
                  as='div'
                  key={option.value}
                  onClick={() => onChangeLanguage(option)}
                  display='block'
                  textAlign='start'
                  withBorder={false}
                  withBackground={false}
                  renderIcon={option.renderBeforeLabel}
                >
                  <span className={cx('subMenuItem', { active: languageSelected === option.value })}>
                    {option.label}
                  </span>
                </BaseButton>
              ))}
            </View>
          )}

          {showLogout && (
            <View>
              <Button renderIcon={<IconToggleStartLine />} onClick={onLogout} textAlign='start'>
                <span>{t('action_logout')}</span>
              </Button>
            </View>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default MobileMenuDialog;
